import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { api, apiError } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { useI18n, formatMoney, formatDate } from "../i18n";
import { PageHeader, Card, Badge } from "../components/shared";
import { Check, CreditCard, Smartphone, Sparkles, Clock, Lock } from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = {
  trial: "info", active: "success", grace: "warning", read_only: "danger", cancelled: "danger",
};

export default function Billing() {
  const { t } = useI18n();
  const { subscription, refreshMe } = useAuth();
  const [plans, setPlans] = useState([]);
  const [txs, setTxs] = useState([]);
  const [loadingPlan, setLoadingPlan] = useState("");
  const location = useLocation();
  const nav = useNavigate();

  const load = async () => {
    const [p, t_] = await Promise.all([api.get("/billing/plans"), api.get("/billing/transactions")]);
    setPlans(p.data.plans);
    setTxs(t_.data);
  };

  useEffect(() => {
    load();
    // Show cancel message
    if (new URLSearchParams(location.search).get("cancelled") === "1") {
      toast.error(t("billing.paymentFailed"));
    }
  }, []);

  const subscribeStripe = async (plan_id) => {
    setLoadingPlan(plan_id + "_stripe");
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/billing/checkout", { plan_id, origin_url: origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(apiError(err, t("common.error")));
    } finally {
      setLoadingPlan("");
    }
  };

  const paySimulated = async (plan_id, method) => {
    setLoadingPlan(`${plan_id}_${method}`);
    try {
      await api.post("/billing/pay-simulated", { plan_id, method });
      toast.success(t("billing.paymentSuccess"));
      await refreshMe();
      await load();
    } catch (err) {
      toast.error(apiError(err, t("common.error")));
    } finally {
      setLoadingPlan("");
    }
  };

  const cancel = async () => {
    if (!window.confirm(t("billing.cancel") + " ?")) return;
    try {
      await api.post("/billing/cancel");
      toast.success(t("common.success"));
      await refreshMe();
    } catch (err) {
      toast.error(apiError(err, t("common.error")));
    }
  };

  const eff = subscription?.effective_status || "trial";
  const currentPlanId = subscription?.plan_id;

  return (
    <div>
      <PageHeader
        title={t("billing.title")}
        subtitle={subscription?.plan?.name}
      />

      {/* Current subscription card */}
      <Card className="p-6 mb-8 bg-gradient-to-br from-brand/5 to-terracotta/5 border-brand/20">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="text-xs text-ink-muted font-semibold uppercase tracking-wider">{t("billing.currentPlan")}</div>
            <div className="mt-1 flex items-baseline gap-3">
              <div className="font-heading font-extrabold text-3xl text-ink">{subscription?.plan?.name}</div>
              <Badge tone={STATUS_TONE[eff] || "neutral"}>{t(`billing.statuses.${eff}`)}</Badge>
            </div>
            <div className="mt-2 text-sm text-ink-muted">
              {subscription?.days_remaining > 0 && (
                <span>{subscription.days_remaining} {t("billing.daysRemaining").toLowerCase()}</span>
              )}
              {subscription?.current_period_end && (
                <span className="ml-3">· {t("billing.renewBy")} {formatDate(subscription.current_period_end)}</span>
              )}
            </div>
          </div>
          {eff !== "cancelled" && (
            <button onClick={cancel} data-testid="sub-cancel" className="h-11 px-4 rounded-xl border-2 border-[#d34044]/40 text-[#b12e31] text-sm font-semibold hover:bg-[#d34044]/10">
              {t("billing.cancel")}
            </button>
          )}
        </div>
      </Card>

      <h2 className="font-heading font-extrabold text-2xl text-ink mb-6">{t("billing.chooseYourPlan")}</h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {plans.map((plan) => {
          const popular = plan.id === "pro";
          const isCurrent = currentPlanId === plan.id && eff === "active";
          return (
            <Card
              key={plan.id}
              className={`p-6 relative flex flex-col ${popular ? "ring-2 ring-brand" : ""}`}
              data-testid={`plan-${plan.id}`}
            >
              {popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-terracotta text-white text-xs font-heading font-bold px-3 py-1 rounded-full">
                  <Sparkles className="w-3 h-3 inline mr-1" />
                  {t("billing.mostPopular")}
                </div>
              )}
              <div className="flex items-baseline justify-between mb-1">
                <h3 className="font-heading font-extrabold text-2xl text-ink">{plan.name}</h3>
                {isCurrent && <Badge tone="success">{t("billing.currentlyOn")}</Badge>}
              </div>
              <div className="mb-4">
                <span className="font-heading font-extrabold text-4xl stat-number text-ink">{formatMoney(plan.price_xof, "XOF")}</span>
                <span className="text-ink-muted"> {t("billing.perMonth")}</span>
              </div>
              <ul className="mb-6 space-y-2 flex-1">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-ink">
                    <Check className="w-4 h-4 text-brand mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="space-y-2">
                <button
                  onClick={() => subscribeStripe(plan.id)}
                  disabled={!!loadingPlan}
                  data-testid={`subscribe-card-${plan.id}`}
                  className={`w-full h-12 rounded-xl font-heading font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-60
                    ${popular ? "bg-brand hover:bg-brand-hover text-white" : "bg-white border-2 border-brand text-brand hover:bg-brand/5"}`}
                >
                  <CreditCard className="w-4 h-4" />
                  {loadingPlan === `${plan.id}_stripe` ? t("common.loading") : t("billing.subscribeCard")}
                </button>
                <button
                  onClick={() => paySimulated(plan.id, "wave")}
                  disabled={!!loadingPlan}
                  data-testid={`subscribe-wave-${plan.id}`}
                  className="w-full h-11 rounded-xl border border-[#2563eb]/30 bg-[#2563eb]/5 hover:bg-[#2563eb]/10 text-[#1e40af] font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  <Smartphone className="w-4 h-4" />
                  {t("billing.subscribeWave")}
                </button>
                <button
                  onClick={() => paySimulated(plan.id, "orange_money")}
                  disabled={!!loadingPlan}
                  data-testid={`subscribe-orange-${plan.id}`}
                  className="w-full h-11 rounded-xl border border-[#e3a03b]/40 bg-[#e3a03b]/10 hover:bg-[#e3a03b]/20 text-[#8a5a10] font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  <Smartphone className="w-4 h-4" />
                  {t("billing.subscribeOrange")}
                </button>
              </div>
            </Card>
          );
        })}
      </div>

      <div className="text-xs text-ink-muted mb-8 italic">{t("billing.simulatedNote")}</div>

      <h2 className="font-heading font-extrabold text-2xl text-ink mb-4">{t("billing.transactions")}</h2>
      <Card className="overflow-hidden">
        {txs.length === 0 ? (
          <div className="p-10 text-center text-ink-muted text-sm">{t("common.noData")}</div>
        ) : (
          <div className="divide-y divide-[var(--border-default)]">
            {txs.map((tx) => (
              <div key={tx.id} className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-brand/10 text-brand flex items-center justify-center">
                  {tx.provider === "stripe" ? <CreditCard className="w-5 h-5" /> : <Smartphone className="w-5 h-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-ink">{formatMoney(tx.amount, tx.currency)} · {tx.plan_id}</div>
                  <div className="text-xs text-ink-muted font-mono">{tx.session_id}</div>
                </div>
                <Badge tone={tx.payment_status === "paid" ? "success" : tx.payment_status === "pending" ? "info" : "warning"}>
                  {tx.payment_status}
                </Badge>
                <span className="text-xs text-ink-muted hidden sm:block">{formatDate(tx.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
